
import { useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { TimeEntry } from "@/types/time-entry";
import { validateTimeRange, calculateDurationSeconds } from "@/utils/timeCalculations";

export function useTimeEntryForm(entry: TimeEntry, onClose: () => void) {
  const [date, setDate] = useState(format(new Date(entry.start_time), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState(format(new Date(entry.start_time), "HH:mm"));
  const [endTime, setEndTime] = useState(entry.end_time ? format(new Date(entry.end_time), "HH:mm") : "");
  const [description, setDescription] = useState(entry.description || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (endTime) {
      const validationError = validateTimeRange(date, startTime, endTime);
      if (validationError) {
        toast({ title: "Fehler", description: validationError, variant: "destructive" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = endTime ? new Date(`${date}T${endTime}`) : null;
      const duration = endDateTime
        ? calculateDurationSeconds(startDateTime.toISOString(), endDateTime.toISOString())
        : null;

      const { error } = await supabase
        .from("time_entries")
        .update({
          start_time: startDateTime.toISOString(),
          end_time: endDateTime?.toISOString() || null,
          duration,
          description,
        })
        .eq("id", entry.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      queryClient.invalidateQueries({ queryKey: ["available-months"] });
      onClose();
      toast({
        title: "Eintrag aktualisiert",
        description: "Der Zeiteintrag wurde erfolgreich aktualisiert",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Fehler beim Aktualisieren des Zeiteintrags",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    date,
    setDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    description,
    setDescription,
    handleSubmit,
    isSubmitting,
  };
}
